import { useQuery } from '@tanstack/react-query';
import { getProducts, getProductBySlug, getSellerProducts } from '../lib/queries';
import type { Badge } from '../data/badges';
import type {
  ChangelogEntry,
  PricingModel,
  ProductCategory,
  SeatTier,
  SetupGuide,
} from '../types';

function parseJSON<T>(raw: unknown, fallback: T): T {
  if (raw == null) return fallback;
  if (typeof raw === 'string') {
    try { return JSON.parse(raw) as T; } catch { return fallback; }
  }
  return raw as T;
}

// Shape returned by Supabase (with joined seller + images)
export type SupabaseProduct = {
  id: string;
  seller_id: string;
  seller: {
    id: string;
    username: string;
    display_name: string | null;
    avatar_url: string | null;
    subscription_tier: string;
    seller_verified: boolean;
  } | null;
  title: string;
  slug: string;
  tagline: string | null;
  description: string | null;
  category: string;
  tags: string[];
  price: number;
  is_free: boolean;
  pricing_model?: PricingModel;
  seat_tiers?: unknown;
  usage_price?: number | string | null;
  usage_unit?: string | null;
  free_units?: number | string | null;
  demo_url: string | null;
  delivery_method: string;
  version: string | null;
  badge_text: string | null;
  status: string;
  sales_count: number;
  view_count: number;
  rating_avg: number;
  rating_count: number;
  images: { url: string; position: number }[];
  is_featured:    boolean;
  is_ai_showcase: boolean;
  featured_until: string | null;
  showcase_until: string | null;
  created_at: string;
  updated_at?: string;
  setup_guide?: unknown;
  whats_included?: unknown;
  compatibility_badges?: unknown;
  changelog?: unknown;
};

// Map a Supabase product → the shape the existing UI components expect
export function toUIProduct(p: SupabaseProduct) {
  const images = (p.images ?? [])
    .sort((a, b) => a.position - b.position)
    .map(i => i.url);

  return {
    id: p.id,
    seller_id: p.seller_id,
    seller: p.seller
      ? {
          id: p.seller.id,
          username: p.seller.username,
          display_name: p.seller.display_name ?? p.seller.username,
          avatar_url: p.seller.avatar_url ?? undefined,
          is_seller: true,
          seller_verified: p.seller.seller_verified,
          subscription_tier: p.seller.subscription_tier as 'free' | 'starter' | 'pro' | 'elite',
          bio: undefined,
          bio_headline: undefined,
          website_url: undefined,
          created_at: '',
        }
      : undefined,
    title: p.title,
    slug: p.slug,
    tagline: p.tagline ?? undefined,
    description: p.description ?? undefined,
    category: p.category as ProductCategory,
    tags: p.tags,
    price: p.price,
    is_free: p.is_free,
    pricing_model: p.pricing_model ?? 'one_time',
    seat_tiers: parseJSON<SeatTier[]>(p.seat_tiers, []),
    usage_price: p.usage_price == null ? undefined : Number(p.usage_price),
    usage_unit: p.usage_unit ?? undefined,
    free_units: p.free_units == null ? 0 : Number(p.free_units),
    demo_url: p.demo_url ?? undefined,
    preview_image_urls: images,
    delivery_method: p.delivery_method as 'download' | 'license_key' | 'hosted_link' | 'api_access',
    version: p.version ?? undefined,
    badge_text: p.badge_text ?? undefined,
    status: p.status as 'draft' | 'published' | 'archived' | 'flagged',
    sales_count: p.sales_count,
    view_count: p.view_count,
    rating_avg: p.rating_avg,
    rating_count: p.rating_count,
    created_at: p.created_at,
    updated_at: p.updated_at ?? p.created_at,
    is_trending:    p.sales_count > 1000,
    is_new:         new Date(p.created_at) > new Date(Date.now() - 7 * 86400000),
    is_featured:    p.is_featured,
    is_ai_showcase: p.is_ai_showcase,
    setup_guide:          parseJSON<SetupGuide | undefined>(p.setup_guide, undefined),
    whats_included:       parseJSON<string[]>(p.whats_included, []).filter(Boolean),
    compatibility_badges: parseJSON<Badge[]>(p.compatibility_badges, []),
    changelog:            parseJSON<ChangelogEntry[]>(p.changelog, []),
  };
}

export function useProducts(params: {
  category?: ProductCategory | null;
  search?: string;
  sort?: 'sales_count' | 'rating_avg' | 'price' | 'created_at';
  limit?: number;
  offset?: number;
} = {}) {
  return useQuery({
    queryKey: ['products', params],
    queryFn: async () => {
      const { data, error } = await getProducts({
        category: params.category ?? undefined,
        search:   params.search   || undefined,
        sort:     params.sort     ?? 'sales_count',
        limit:    params.limit    ?? 24,
        offset:   params.offset   ?? 0,
      });
      if (error) throw error;
      return (data as SupabaseProduct[]).map(toUIProduct);
    },
    placeholderData: (prev) => prev,
  });
}

export function useProduct(slug: string) {
  return useQuery({
    queryKey: ['product', slug],
    queryFn: async () => {
      const { data, error } = await getProductBySlug(slug);
      if (error) throw error;
      if (!data) throw new Error('Product not found');
      return toUIProduct(data as unknown as SupabaseProduct);
    },
    enabled: !!slug,
  });
}

export function useSellerProducts(sellerId: string) {
  return useQuery({
    queryKey: ['products', 'seller', sellerId],
    queryFn: async () => {
      const { data, error } = await getSellerProducts(sellerId);
      if (error) throw error;
      return (data as unknown as SupabaseProduct[]).map(toUIProduct);
    },
    enabled: !!sellerId,
  });
}
